// =============================================================================
// The create/edit event modal: title with suggestions from earlier events,
// label, participants (shared calendars), the iOS-style all-day / start / end
// block, notes, repeat rule and sport link. The draft (`newEvent`) and the
// save / delete logic stay in Calendar.js; this renders and edits the draft.
// =============================================================================

import React, { useState, useMemo } from 'react';
import { X, Trash2, Repeat } from 'lucide-react';
import { formatDate as fmt } from '../../lib/dates';
import { useTranslation } from '../../lib/i18n';
import { suggestEvents, applySuggestionTo } from '../../lib/eventSuggestions';
import LabelDropdown from './LabelDropdown';
import ParticipantPicker from './ParticipantPicker';
import RecurrenceEditor from './RecurrenceEditor';
import SportLinker from './SportLinker';
import TimeField from './TimeField';
import { endTimeAfterStartChange } from './utils';

const EventForm = ({
  editingEvent,
  newEvent,
  setNewEvent,
  isSharedView,
  membersById,
  renderCreatorAvatar,
  calendarEvents,
  getCategory,
  selectableCategories,
  sports,
  onSave,
  onDelete,
  onClose
}) => {
  const { t } = useTranslation();

  // --- Title suggestions ------------------------------------------------
  // Typing a title offers earlier events in this calendar with a matching
  // name; picking one copies its label, all-day flag and times into the
  // *new* event so a recurring appointment is two taps instead of a form.
  // The source event is never touched — it is a template, not a link.
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const titleSuggestions = useMemo(
    () => (editingEvent ? [] : suggestEvents(calendarEvents, newEvent.title)),
    [calendarEvents, newEvent.title, editingEvent]
  );

  const applySuggestion = (ev) => {
    setNewEvent(applySuggestionTo(newEvent, ev));
    setSuggestionsOpen(false);
  };

  const suggestionMeta = (ev) => {
    const when = t('calendar.suggestLast', 'Zuletzt {date}', { date: fmt(ev.date, 'short') });
    const time = ev.allDay
      ? t('dashboard.allDay', 'ganztägig')
      : `${ev.startTime || ''}${ev.endTime ? '–' + ev.endTime : ''}`;
    return time ? `${when} · ${time}` : when;
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl p-5 sm:p-6 w-full max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {editingEvent ? t('calendar.editEvent') : t('calendar.newEvent')}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        {isSharedView && editingEvent && editingEvent.createdBy && (
          <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            {renderCreatorAvatar(editingEvent.createdBy, 22)}
            <span>
              {t('sharedCalendar.createdBy')}:{' '}
              <span className="font-medium text-gray-800 dark:text-gray-100">
                {membersById[editingEvent.createdBy]?.displayName ||
                  t('sharedCalendar.unknownUser')}
              </span>
            </span>
          </div>
        )}

        {editingEvent && editingEvent.recurrence && editingEvent.recurrence.freq && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2 text-xs">
            <Repeat size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-blue-900">
              <p className="font-medium">
                {t('calendar.editingSeries', 'Serie wird bearbeitet')}
              </p>
              <p className="text-blue-700">
                {t('calendar.clickedOn', 'Geklickt auf')}:{' '}
                {fmt(editingEvent.clickedOccurrenceDate || editingEvent.date, 'long')}
                {editingEvent.clickedOccurrenceDate !== editingEvent.date && (
                  <>
                    {' '}
                    · {t('calendar.seriesStart', 'Serien‑Start')}:{' '}
                    {fmt(editingEvent.date, 'short')}
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.title')}</label>
            <input
              type="text"
              autoFocus={!editingEvent}
              placeholder={t('calendar.titlePlaceholder')}
              className="input"
              value={newEvent.title}
              onChange={(e) => {
                setNewEvent({ ...newEvent, title: e.target.value });
                setSuggestionsOpen(true);
              }}
              onFocus={() => setSuggestionsOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setSuggestionsOpen(false);
              }}
            />
            {/* Inline (not floating) so it can never be clipped by the
                scrolling sheet, and so the form simply grows to fit. */}
            {suggestionsOpen && titleSuggestions.length > 0 && (
              <ul
                className="mt-1 rounded-lg border border-gray-200 bg-white divide-y divide-gray-100 overflow-hidden"
                role="listbox"
              >
                {titleSuggestions.map((ev) => {
                  const cat = getCategory(ev.categoryId);
                  return (
                    <li key={ev.id} role="option" aria-selected={false}>
                      <button
                        type="button"
                        // preventDefault keeps the input focused through the
                        // tap, so the list is still there when the click lands.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => applySuggestion(ev)}
                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 active:bg-gray-100"
                      >
                        <span
                          className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: cat.color }}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-gray-900">
                            {ev.title}
                          </span>
                          <span className="block truncate text-xs text-gray-500">
                            {suggestionMeta(ev)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('calendar.fieldLabel')}</label>
            <LabelDropdown
              categories={selectableCategories}
              value={newEvent.categoryId}
              onChange={(id) => setNewEvent({ ...newEvent, categoryId: id })}
            />
          </div>

          {isSharedView && (
            <ParticipantPicker
              members={Object.values(membersById)}
              value={newEvent.participantIds || []}
              onChange={(ids) =>
                setNewEvent({ ...newEvent, participantIds: ids })
              }
              renderAvatar={renderCreatorAvatar}
              t={t}
            />
          )}

          {/* iOS-style time block: all-day toggle + Beginn/Ende rows
              with date + (optional) time pills aligned to the right. */}
          <div className="rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 divide-y divide-gray-200 dark:divide-gray-800">
            <div className="flex items-center justify-between px-3 py-2.5">
              <label htmlFor="allDay" className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {t('calendar.allDay')}
              </label>
              <button
                type="button"
                role="switch"
                aria-checked={newEvent.allDay}
                onClick={() =>
                  setNewEvent({ ...newEvent, allDay: !newEvent.allDay })
                }
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  newEvent.allDay ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    newEvent.allDay ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center gap-2 px-3 py-2.5">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{t('calendar.begin')}</span>
              <div className="ml-auto flex items-center gap-2 min-w-0">
                <input
                  type="date"
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-w-0 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                />
                {!newEvent.allDay && (
                  <TimeField
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-w-0 w-[6.5rem] focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={newEvent.startTime}
                    ariaLabel={t('calendar.begin')}
                    onChange={(startTime) =>
                      setNewEvent({
                        ...newEvent,
                        startTime,
                        // The end moves with the start so the length stays.
                        endTime: endTimeAfterStartChange(newEvent.startTime, newEvent.endTime, startTime)
                      })
                    }
                  />
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 px-3 py-2.5">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{t('common.end')}</span>
              <div className="ml-auto flex items-center gap-2 min-w-0">
                <input
                  type="date"
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-w-0 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={newEvent.endDate || newEvent.date}
                  min={newEvent.date}
                  onChange={(e) =>
                    setNewEvent({ ...newEvent, endDate: e.target.value })
                  }
                />
                {!newEvent.allDay && (
                  <TimeField
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 text-sm text-gray-900 dark:text-gray-100 min-w-0 w-[6.5rem] focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={newEvent.endTime}
                    ariaLabel={t('common.end')}
                    onChange={(endTime) => setNewEvent({ ...newEvent, endTime })}
                  />
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notes')}</label>
            <textarea
              placeholder={t('todo.notePlaceholder')}
              className="input"
              rows={3}
              value={newEvent.notes}
              onChange={(e) => setNewEvent({ ...newEvent, notes: e.target.value })}
            />
          </div>

          <RecurrenceEditor
            date={newEvent.date}
            recurrence={newEvent.recurrence}
            setRecurrence={(rec) => setNewEvent({ ...newEvent, recurrence: rec })}
          />

          <SportLinker
            sports={sports}
            linkedSportName={newEvent.linkedSportName || ''}
            setLinkedSportName={(name) =>
              setNewEvent({ ...newEvent, linkedSportName: name || null })
            }
          />

          <div className="flex gap-2 pt-2">
            <button
              onClick={onSave}
              className="btn bg-primary-600 text-white hover:bg-primary-700 flex-1"
            >
              {editingEvent ? t('common.save') : t('common.add')}
            </button>
            {editingEvent && (
              <button
                onClick={() => onDelete(editingEvent.id)}
                className="btn bg-red-100 text-red-600 hover:bg-red-200 flex items-center gap-1"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventForm;
